import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import { FormattedMessage } from 'react-intl';
import { useForm } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import { IDataTable } from '@shared/models/data-table.model';
import { useIsConfirmed } from '@client/hooks/use-confirm.hook';
import { useCreateDataTable, useUpdateDataTable } from '../store';
import {
  closeDataTableDialog,
  useDataTableDialogStore,
} from './data-table-dialog.store';
import { omitUndefined } from '@shared/helpers/object.helpers';
import { extractKeysFromData } from './data-table-dialog.helpers';
import { isValidVariableName } from '@shared/helpers/mathjs';
import { parseDataTableData } from '@shared/helpers/data-tables';

interface IDataTableDialogProps<T extends IDataTable | undefined> {
  open: boolean;
  onClose: (dataTable?: IDataTable) => void;
  dataTable?: T;
}

interface IFormData {
  name: string;
  data: string;
  index_key: string;
}

interface IIndexKeySelectorProps {
  availableKeys: string[];
  indexKey: string;
  onIndexKeyChange: (key: string) => void;
}

const IndexKeySelector: React.FC<IIndexKeySelectorProps> = ({
  availableKeys,
  indexKey,
  onIndexKeyChange,
}) => {
  return (
    <FormControl fullWidth margin="normal">
      <InputLabel>
        <FormattedMessage
          id="data_table.index_key"
          defaultMessage="Index Key"
        />
      </InputLabel>
      <Select
        value={indexKey}
        onChange={(e) => onIndexKeyChange(e.target.value)}
        label={
          <FormattedMessage
            id="data_table.index_key"
            defaultMessage="Index Key"
          />
        }
      >
        {availableKeys.map((key) => (
          <MenuItem key={key} value={key}>
            {key}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface IKeyListProps {
  availableKeys: string[];
  selectedKeys: Set<string>;
  indexKey: string;
  onKeyToggle: (key: string) => void;
}

const KeyList: React.FC<IKeyListProps> = ({
  availableKeys,
  selectedKeys,
  indexKey,
  onKeyToggle,
}) => {
  const { classes } = useStyles();

  return (
    <Box className={classes.selectedKeysSummary}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
        }}
      >
        <FormattedMessage
          id="data_table.available_keys"
          defaultMessage="Available Keys"
        />
      </Typography>
      <List dense className={classes.selectedKeysList}>
        {availableKeys.map((key) => (
          <ListItem key={key} className={classes.selectedKeyItem} dense>
            <Checkbox
              checked={selectedKeys.has(key) && key !== indexKey}
              disabled={key === indexKey}
              onChange={() => onKeyToggle(key)}
              size="small"
            />
            <ListItemText primary={key} />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

const DataTableDialogCore = <T extends IDataTable | undefined>({
  open,
  onClose,
  dataTable,
}: IDataTableDialogProps<T>) => {
  const { classes } = useStyles();
  const confirm = useIsConfirmed();
  const createDataTable = useCreateDataTable();
  const updateDataTable = useUpdateDataTable();

  type PayloadType = typeof dataTable extends undefined
    ? Parameters<typeof createDataTable>[0]
    : Parameters<typeof updateDataTable>[0];
  const send = (dataTable ? updateDataTable : createDataTable) as (
    payload: PayloadType,
  ) => Promise<IDataTable>;
  const id = dataTable?.id;
  const defaultName = dataTable?.name ?? '';
  const dataText = JSON.stringify(dataTable?.data ?? [], undefined, 4);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IFormData>({
    defaultValues: { name: defaultName, data: dataText },
  });

  const nameInputRef = useRef<HTMLInputElement>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [index_key, setIndexKey] = useState<string>('');

  // Track initial values for dirty state calculation
  const [initialValues, setInitialValues] = useState({
    name: '',
    data: '[]',
    index_key: '',
    selectedKeys: new Set<string>(),
  });

  // Custom dirty state that includes all form data
  const isFormDirty = useCallback(() => {
    const currentName = watch('name');
    const currentData = watch('data');

    return (
      currentName !== initialValues.name ||
      currentData !== initialValues.data ||
      index_key !== initialValues.index_key ||
      JSON.stringify(Array.from(selectedKeys).sort()) !==
        JSON.stringify(Array.from(initialValues.selectedKeys).sort())
    );
  }, [watch, index_key, selectedKeys, initialValues]);

  // Update selected keys happens on blur to avoid excessive parsing

  // Handle key changes on blur
  const handleDataInputBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const dataString = event.target.value;
      const availableKeys = extractKeysFromData(dataString);

      // Keep only selected keys that still exist in the data
      const newSelectedKeys = new Set<string>();
      selectedKeys.forEach((key) => {
        if (availableKeys.includes(key)) {
          newSelectedKeys.add(key);
        }
      });

      setSelectedKeys(newSelectedKeys);
    },
    [selectedKeys],
  );

  // Handle key selection toggle
  const handleKeyToggle = useCallback((key: string) => {
    setSelectedKeys((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }, []);

  // Handle index key change
  const handleIndexKeyChange = useCallback(
    (key: string) => {
      setIndexKey(key);
      setValue('index_key', key, { shouldDirty: true, shouldValidate: true });
    },
    [setValue],
  );

  // Reset the form when the dialog is opened
  useEffect(() => {
    if (open) {
      const initialName = dataTable?.name ?? '';
      const initialData = dataTable?.data
        ? JSON.stringify(dataTable.data, undefined, 4)
        : '[]';
      const initialIndexKey = dataTable?.index_key ?? '';

      reset({
        name: initialName,
        data: initialData,
        index_key: initialIndexKey,
      });

      // Reset selected keys based on current JSON data
      const availableKeys = extractKeysFromData(initialData);
      const initialSelectedKeys = dataTable
        ? new Set(
            availableKeys.filter((key) => dataTable.properties?.includes(key)),
          )
        : new Set(availableKeys);
      setSelectedKeys(initialSelectedKeys);

      // Set index key if it exists
      setIndexKey(initialIndexKey);
      setValue('index_key', initialIndexKey, {
        shouldDirty: false,
        shouldValidate: false,
      });

      // Store initial values for dirty state tracking
      setInitialValues({
        name: initialName,
        data: initialData,
        index_key: initialIndexKey,
        selectedKeys: initialSelectedKeys,
      });

      nameInputRef.current?.focus();
    }
  }, [open, dataTable, reset, setValue]);

  const getPayloadFromFormData = useCallback(
    ({ name, data, index_key }: IFormData): PayloadType => {
      const selected = Array.from(selectedKeys);
      const properties = selected.filter((key) => key !== index_key);

      return omitUndefined({
        id,
        name,
        data: parseDataTableData(data),
        index_key,
        properties,
      }) as PayloadType;
    },
    [id, selectedKeys],
  );

  const onSubmit = useCallback(
    async ({
      name,
      data,
      index_key: formIndexKey,
    }: IFormData): Promise<IDataTable> => {
      // Ensure index_key is selected
      if (!formIndexKey) {
        throw new Error('Index key is required');
      }
      const payload = getPayloadFromFormData({
        name,
        data,
        index_key: formIndexKey,
      });
      const responseTable = await send(payload);
      onClose(responseTable);
      return responseTable;
    },
    [getPayloadFromFormData, onClose, send],
  );

  const handleClose = useCallback(async () => {
    if (
      !isFormDirty() ||
      (await confirm({
        title: 'Unsaved changes',
        content: 'You have unsaved changes. Are you sure you want to close?',
      }))
    ) {
      onClose();
    }
  }, [confirm, isFormDirty, onClose]);

  const validateDataInput = useCallback((value: string) => {
    if (!value.trim()) return 'JSON data is required';

    try {
      const parsed = JSON.parse(value);
      if (!Array.isArray(parsed)) {
        return 'JSON data must be an array';
      }
      if (parsed.length === 0) {
        return 'JSON data must contain at least one item';
      }
      return true;
    } catch {
      return 'Invalid JSON format';
    }
  }, []);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          <FormattedMessage
            id={dataTable ? 'data_table.edit.title' : 'data_table.create.title'}
            defaultMessage={dataTable ? 'Edit Data Table' : 'Create Data Table'}
          />
        </DialogTitle>
        <DialogContent>
          <Box className={classes.content}>
            <Box className={classes.mainLayout}>
              {/* Left Panel - Configuration */}
              <Box className={classes.leftPanel}>
                <TextField
                  {...register('name', {
                    required: 'Name is required',
                    validate: (value) =>
                      isValidVariableName(value) ||
                      'Name must be a valid variable name',
                    minLength: {
                      value: 3,
                      message: 'Name must be at least 3 characters',
                    },
                  })}
                  inputRef={nameInputRef}
                  label={
                    <FormattedMessage
                      id="data_table.name"
                      defaultMessage="Name"
                    />
                  }
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                  margin="normal"
                />

                {/* Key Selection Section */}
                <Box className={classes.keySelectionSection}>
                  <Typography
                    variant="subtitle2"
                    className={classes.keySelectionTitle}
                  >
                    <FormattedMessage
                      id="data_table.available_keys"
                      defaultMessage="Available Keys"
                    />
                  </Typography>

                  {(() => {
                    const availableKeys = extractKeysFromData(watch('data'));
                    if (availableKeys.length === 0) {
                      return (
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          <FormattedMessage
                            id="data_table.no_keys_found"
                            defaultMessage="No keys found in JSON data"
                          />
                        </Typography>
                      );
                    }

                    return (
                      <Box className={classes.keySelectionContent}>
                        <IndexKeySelector
                          availableKeys={availableKeys}
                          indexKey={index_key}
                          onIndexKeyChange={handleIndexKeyChange}
                        />

                        <KeyList
                          availableKeys={availableKeys}
                          selectedKeys={selectedKeys}
                          indexKey={index_key}
                          onKeyToggle={handleKeyToggle}
                        />
                      </Box>
                    );
                  })()}
                </Box>
              </Box>

              {/* Right Panel - JSON Data Editor */}
              <Box className={classes.rightPanel}>
                <TextField
                  {...register('data', {
                    validate: validateDataInput,
                  })}
                  label={
                    <FormattedMessage
                      id="data_table.data"
                      defaultMessage="JSON Data"
                    />
                  }
                  error={!!errors.data}
                  helperText={
                    errors.data?.message ||
                    'Enter JSON array data (e.g., [{"key": "value"}])'
                  }
                  fullWidth
                  multiline
                  minRows={8}
                  maxRows={20}
                  margin="normal"
                  className={classes.dataField}
                  onBlur={handleDataInputBlur}
                  slotProps={{
                    htmlInput: {
                      style: { fontFamily: 'monospace', fontSize: '14px' },
                    },
                  }}
                />
                {/* Hidden field for index_key so it participates in react-hook-form validation */}
                <input
                  {...register('index_key', {
                    required: 'Index key is required',
                  })}
                  type="hidden"
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>
            <FormattedMessage id="data_table.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isFormDirty() && !dataTable}
          >
            <FormattedMessage
              id={dataTable ? 'data_table.save' : 'data_table.create'}
              defaultMessage={dataTable ? 'Save' : 'Create'}
            />
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const useStyles = makeStyles()(({ spacing }) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 400,
  },
  mainLayout: {
    display: 'flex',
    gap: spacing(2),
    minHeight: 400,
  },
  leftPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  rightPanel: {
    flex: 3,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 250,
  },
  dataField: {
    '& .MuiInputBase-input': {
      fontFamily: 'monospace',
      fontSize: '14px',
    },
    flex: 1,
  },
  keySelectionSection: {
    marginTop: 0,
  },
  keySelectionTitle: {
    fontWeight: 'bold',
  },
  keySelectionContent: {
    marginTop: 1,
  },
  keyCheckboxes: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
  },
  keyCheckbox: {
    marginRight: 4,
  },
  selectedKeysSummary: {
    marginTop: 1,
  },
  selectedKeysList: {
    maxHeight: 200,
    overflow: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: 1,
    backgroundColor: '#fafafa',
  },
  selectedKeyItem: {
    padding: '2px 8px',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  },
}));

/**
 * Exported container that prevents rendering dialog when not open.
 * @returns
 */
export const DataTableDialog: React.FC = () => {
  const { isOpen, dataTable } = useDataTableDialogStore();

  if (!isOpen) {
    return null;
  }

  return (
    <DataTableDialogCore
      open={isOpen}
      dataTable={dataTable}
      onClose={closeDataTableDialog}
    />
  );
};
